import { HeadingNode, HeadingTagType, SerializedHeadingNode } from '@lexical/rich-text';
import { createCommentableNode } from '../CommentableMixin';
import {
  EditorConfig,
  ElementFormatType,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode
} from 'lexical';
import { v4 as uuidv4 } from 'uuid';
import ReactionArticleIcon from '../../../../assets/reaction-article-icon.svg';

export type SerializedCommentableHeadingNode = {
  type: string;
  version: number;
  tag: HeadingTagType;
  nodeId: string;
  commentCount: number;
  reactionCount: number;
  nodeData: {
    reactionCount: number;
    isReacted: boolean;
    isCommented: boolean;
    reactedType?: string;
    comment?: {
      isCommented: boolean;
      commentCount: number;
    };
  };
  children: Array<SerializedLexicalNode>;
  direction: 'ltr' | 'rtl' | null;
  format: ElementFormatType;
  indent: number;
};

const CommentableHeadingNodeBase = createCommentableNode(HeadingNode);

export class CommentableHeadingNode extends CommentableHeadingNodeBase {
  static getType(): string {
    return 'commentable-heading';
  }

  static clone(node: CommentableHeadingNode): CommentableHeadingNode {
    return new CommentableHeadingNode(
      node.__key,
      node.__nodeId,
      node.__commentCount,
      node.__reactionCount,
      node.__nodeData,
      node.__tag
    );
  }

  constructor(
    key?: NodeKey,
    nodeId?: string,
    commentCount: number = 0,
    reactionCount: number = 0,
    nodeData: any = {},
    tag: HeadingTagType = 'h1'
  ) {
    // @ts-ignore
    super(key);
    this.__nodeId = nodeId || uuidv4();
    this.__commentCount = commentCount;
    this.__reactionCount = reactionCount;
    this.__nodeData = nodeData;
    this.__tag = tag;

    if (key !== undefined) {
      this.__key = key;
    }
  }

  override createDOM(config: EditorConfig, editor?: LexicalEditor): HTMLElement {
    const dom = super.createDOM(config, editor);
    const className = config.theme.heading?.[this.__tag] ?? '';
    if (className) {
      dom.className = className + ' ' + dom.className;
    }
    return dom;
  }

  updateDOM(prevNode: CommentableHeadingNode, dom: HTMLElement, config: EditorConfig): boolean {
    const baseInstance = this as any;

    const currentNodeData = this.getNodeData();
    const prevNodeData = prevNode.getNodeData();

    const reactionButton = dom.querySelector('.reactions-button') as HTMLElement;
    const commentButton = dom.querySelector('.comment-button') as HTMLElement;
    const countButton = dom.querySelector('.reaction-count-button') as HTMLElement;

    if (!reactionButton || !commentButton || !countButton) {
      return true;
    }

    // Update Reaction Button
    const isReacted = currentNodeData?.isReacted && currentNodeData?.reactedType;
    const wasReacted = prevNodeData?.isReacted && prevNodeData?.reactedType;
    const reactionTypeChanged = currentNodeData?.reactedType !== prevNodeData?.reactedType;

    if (isReacted === wasReacted || reactionTypeChanged) {
      const reactionState = reactionButton.querySelector('.reaction-state') as HTMLElement;
      if (reactionState) {
        reactionState.innerHTML = ''; // Clear only the reaction state
        reactionButton.classList.remove('reacted');

        if (isReacted) {
          const selectedEmoji = baseInstance.getReactionIconByType(currentNodeData.reactedType);
          if (selectedEmoji) {
            reactionButton.classList.add('reacted');
            const img = document.createElement('img');
            img.classList.add('image-reacted');
            img.src = selectedEmoji.icon;
            img.alt = selectedEmoji.textDisplay;
            reactionState.appendChild(img);
            const span = document.createElement('span');
            span.classList.add('text-reacted');
            span.textContent = selectedEmoji.textDisplay;
            reactionState.appendChild(span);
          }
        } else {
          const img = document.createElement('img');
          img.classList.add('reaction-icon');
          img.src = ReactionArticleIcon;
          img.alt = 'Reaction Icon';
          reactionState.appendChild(img);
          const plus = document.createElement('span');
          plus.classList.add('reaction-plus');
          plus.textContent = '+';
          reactionState.appendChild(plus);
        }
      }
    }

    const isCommented = currentNodeData?.comment?.isCommented || false;
    const wasCommented = prevNodeData?.comment?.isCommented || false;
    if (isCommented !== wasCommented) {
      const commentIcon = commentButton.querySelector('.comment-icon') as HTMLImageElement;
      if (commentIcon) {
        if (isCommented) {
          commentButton.style.backgroundColor = '#4CAF50';
          commentIcon.style.filter = 'brightness(0) invert(1)';
        } else {
          commentButton.style.backgroundColor = '';
          commentIcon.style.filter = '';
        }
      }
    }

    const currentCount = baseInstance.getTotalCount();
    const prevCount = (prevNode as any).getTotalCount();
    if (currentCount !== prevCount) {
      const countText = countButton.querySelector('.reaction-count-text') as HTMLElement;
      if (countText) {
        countText.textContent = currentCount;
      }
    }

    return false;
  }

  exportJSON(): SerializedCommentableHeadingNode {
    const baseJSON = super.exportJSON() as SerializedHeadingNode;
    return {
      ...baseJSON,
      type: 'commentable-heading',
      version: 1,
      tag: this.__tag,
      nodeId: this.__nodeId,
      commentCount: this.__commentCount,
      reactionCount: this.__reactionCount,
      nodeData: this.__nodeData,
      children: baseJSON.children,
      direction: baseJSON.direction,
      format: baseJSON.format,
      indent: baseJSON.indent
    };
  }

  static importJSON(serializedNode: SerializedCommentableHeadingNode): CommentableHeadingNode {
    const node = new CommentableHeadingNode(
      undefined,
      serializedNode.nodeId,
      serializedNode.commentCount,
      serializedNode.reactionCount,
      serializedNode.nodeData,
      serializedNode.tag
    );

    node.setFormat(serializedNode.format as ElementFormatType);
    node.setIndent(serializedNode.indent);
    node.setDirection(serializedNode.direction);

    return node;
  }
}

export function $createCommentableHeadingNode(
  tag: HeadingTagType,
  commentCount: number = 0,
  nodeId?: string
): CommentableHeadingNode {
  return new CommentableHeadingNode(undefined, nodeId, commentCount, 0, {}, tag);
}

export function $isCommentableHeadingNode(
  node: LexicalNode | null | undefined
): node is CommentableHeadingNode {
  return node instanceof CommentableHeadingNode;
}
